/**
 * ARIA Job Details Modal Component
 * Version: 1.0.0
 *
 * Displays comprehensive job details in a modal/drawer
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  X,
  MapPin,
  User,
  Phone,
  Mail,
  Clock,
  Calendar,
  Wrench,
  Package,
  FileText,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Clipboard,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Job, JobType, JobStatus, PartsStatus, TransferItem } from '../../types';
import { IdentifierBadge } from './IdentifierBadge';
import { SortableHeader } from '../table/SortableHeader';
import { compareValues, type SortDirection, type SortValueType } from '../../utils/sort';

// Full job details query
const JOB_DETAILS_QUERY = gql`
  query JobDetails($id: ID!) {
    job(id: $id) {
      id
      sytelineOrderId
      sytelineOrderNumber
      seNumber
      jobType
      status
      priority
      identifierType
      customerName
      customerEmail
      customerPhone
      addressLine1
      addressLine2
      city
      province
      postalCode
      fullAddress
      zone {
        id
        name
      }
      scheduling {
        scheduledDate
        startTime
        endTime
        appointmentDescription
      }
      estimatedDurationMinutes
      technicians {
        id
        fullName
        email
        phone
      }
      assignedBy
      assignmentReason
      parts {
        status
        eta
        notes
        shippedDate
        receivedDate
        transferOrders {
          id
          transferNumber
          status
          eta
          shippedDate
          receivedDate
          items {
            id
            partNumber
            partName
            qtyRequired
            qtyShipped
            qtyReceived
            status
          }
        }
      }
      description
      jobSummary
      internalNotes
      requiredSkills
      pendingDebrief
      sytelineBillStatus
      warranty {
        isWarrantyJob
        originalOrderId
        serviceDates
        resolutionNotes
      }
      createdAt
      updatedAt
    }
  }
`;

// Complete job mutation
const COMPLETE_JOB_MUTATION = gql`
  mutation CompleteJob($id: ID!) {
    completeJob(id: $id) {
      id
      status
      completedAt
    }
  }
`;

type PartsItemSortField = 'partNumber' | 'qtyRequired' | 'qtyShipped' | 'qtyReceived' | 'status';

const PARTS_SORT_ACCESSORS: Record<PartsItemSortField, { get: (item: TransferItem) => unknown; type: SortValueType }> = {
  partNumber: { get: (item) => item.partName ?? item.partNumber, type: 'string' },
  qtyRequired: { get: (item) => item.qtyRequired, type: 'number' },
  qtyShipped: { get: (item) => item.qtyShipped, type: 'number' },
  qtyReceived: { get: (item) => item.qtyReceived, type: 'number' },
  status: { get: (item) => item.status, type: 'string' },
};

// A transfer order's parts table — pulled out of the transferOrders.map() loop
// so each transfer order can own its own independent sort state (hooks can't
// be called inside a .map callback in the parent component).
function TransferOrderItemsTable({ items }: { items: TransferItem[] }) {
  const [sortField, setSortField] = useState<PartsItemSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: string) => {
    const typedField = field as PartsItemSortField;
    setSortDirection((prev) => (sortField === typedField && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(typedField);
  };

  const sortedItems = useMemo(() => {
    if (!sortField) return items;
    const { get, type } = PARTS_SORT_ACCESSORS[sortField];
    return [...items].sort((a, b) => compareValues(get(a), get(b), sortDirection, type));
  }, [items, sortField, sortDirection]);

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500 border-b">
          <SortableHeader label="Part" field="partNumber" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
          <SortableHeader label="Req" field="qtyRequired" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} align="right" />
          <SortableHeader label="Shipped" field="qtyShipped" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} align="right" />
          <SortableHeader label="Received" field="qtyReceived" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} align="right" />
          <SortableHeader label="Status" field="status" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {sortedItems.map((item) => (
          <tr key={item.id}>
            <td className="py-2">
              <p className="font-medium">{item.partName ?? item.partNumber}</p>
              <p className="text-xs text-gray-400">{item.partNumber}</p>
            </td>
            <td className="py-2 text-center">{item.qtyRequired}</td>
            <td className="py-2 text-center">{item.qtyShipped ?? '-'}</td>
            <td className="py-2 text-center">{item.qtyReceived ?? '-'}</td>
            <td className="py-2 capitalize">{item.status ?? '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface JobDetailsModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onJobCompleted?: () => void;
}

const jobTypeLabels: Record<JobType, string> = {
  installation: 'Installation',
  service: 'Service',
  warranty: 'Warranty',
  inspection: 'Inspection',
  callback: 'Callback',
  estimate: 'Estimate',
};

const jobTypeColors: Record<JobType, string> = {
  installation: 'bg-blue-100 text-blue-700',
  service: 'bg-green-100 text-green-700',
  warranty: 'bg-orange-100 text-orange-700',
  inspection: 'bg-purple-100 text-purple-700',
  callback: 'bg-red-100 text-red-700',
  estimate: 'bg-gray-100 text-gray-700',
};

const statusLabels: Record<JobStatus, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  PENDING_PARTS: 'Pending Parts',
  READY_TO_SCHEDULE: 'Ready to Schedule',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On Hold',
  PENDING_DEBRIEF: 'Pending Debrief',
};

const statusColors: Record<JobStatus, string> = {
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PENDING_PARTS: 'bg-orange-100 text-orange-700',
  READY_TO_SCHEDULE: 'bg-cyan-100 text-cyan-700',
  SCHEDULED: 'bg-indigo-100 text-indigo-700',
  IN_PROGRESS: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  ON_HOLD: 'bg-gray-100 text-gray-700',
  PENDING_DEBRIEF: 'bg-amber-100 text-amber-700',
};

const partsStatusLabels: Record<PartsStatus, string> = {
  not_required: 'Not Required',
  available: 'Available',
  partial: 'Partial',
  backordered: 'Backordered',
  pending: 'Pending',
  shipped: 'Shipped',
  received: 'Received',
};

const partsStatusColors: Record<PartsStatus, string> = {
  not_required: 'bg-gray-100 text-gray-700',
  available: 'bg-green-100 text-green-700',
  partial: 'bg-yellow-100 text-yellow-700',
  backordered: 'bg-red-100 text-red-700',
  pending: 'bg-orange-100 text-orange-700',
  shipped: 'bg-blue-100 text-blue-700',
  received: 'bg-green-100 text-green-700',
};

export function JobDetailsModal({ jobId, isOpen, onClose, onJobCompleted }: JobDetailsModalProps) {
  const { data, loading, error } = useQuery(JOB_DETAILS_QUERY, {
    variables: { id: jobId },
    skip: !isOpen || !jobId,
  });

  const [completeJob, { loading: completing }] = useMutation(COMPLETE_JOB_MUTATION, {
    onCompleted: () => {
      onJobCompleted?.();
      onClose();
    },
  });

  if (!isOpen) return null;

  const job = data?.job as Job | undefined;

  const handleCompleteJob = async () => {
    if (!job) return;
    try {
      await completeJob({ variables: { id: job.id } });
    } catch (err) {
      console.error('Failed to complete job:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Calculate elapsed time for in-progress jobs
  const startTime = job?.scheduling?.startTime ? new Date(job.scheduling.startTime) : null;
  const elapsed = startTime ? Math.floor((Date.now() - startTime.getTime()) / (1000 * 60)) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Job Details</h2>
            {job && (
              <p className="text-sm text-gray-500">
                {job.seNumber || job.sytelineOrderNumber}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading job details...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-500 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Failed to load job details
              </div>
            </div>
          ) : job ? (
            <div className="space-y-6">
              {/* Job Identification */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {job.seNumber || job.sytelineOrderNumber}
                    </h3>
                    <IdentifierBadge type={job.identifierType} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={clsx('badge', jobTypeColors[job.jobType])}>
                      {jobTypeLabels[job.jobType]}
                    </span>
                    <span className={clsx('badge', statusColors[job.status])}>
                      {statusLabels[job.status]}
                    </span>
                  </div>
                </div>

                {/* In Progress Timer */}
                {job.status === 'IN_PROGRESS' && startTime && elapsed !== null && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-sm font-medium text-purple-900">
                        Job in progress for {elapsed} minutes
                      </p>
                      <p className="text-xs text-purple-600">
                        Started at {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {job.estimatedDurationMinutes && (
                          <span> • Est. duration: {job.estimatedDurationMinutes} min</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* Customer Information */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-lg font-medium text-gray-900">{job.customerName || 'N/A'}</p>
                  </div>

                  {job.customerPhone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${job.customerPhone}`} className="hover:text-brand-600">
                        {job.customerPhone}
                      </a>
                      <button
                        onClick={() => copyToClipboard(job.customerPhone!)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Copy phone number"
                      >
                        <Clipboard className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  )}

                  {job.customerEmail && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${job.customerEmail}`} className="hover:text-brand-600">
                        {job.customerEmail}
                      </a>
                    </div>
                  )}
                </div>
              </section>

              {/* Location */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </h4>
                <div className="space-y-2">
                  <p className="text-gray-900">{job.addressLine1}</p>
                  {job.addressLine2 && <p className="text-gray-600">{job.addressLine2}</p>}
                  <p className="text-gray-600">
                    {job.city}, {job.province} {job.postalCode}
                  </p>
                  {job.zone && (
                    <p className="text-sm text-gray-500">
                      Zone: <span className="font-medium">{job.zone.name}</span>
                    </p>
                  )}
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(job.fullAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 mt-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Google Maps
                  </a>
                </div>
              </section>

              {/* Scheduling */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Scheduling
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Scheduled Date</p>
                    <p className="text-gray-900">
                      {job.scheduling?.scheduledDate
                        ? new Date(job.scheduling.scheduledDate).toLocaleDateString('en-CA', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'Not scheduled'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Time</p>
                    <p className="text-gray-900">
                      {job.scheduling?.startTime
                        ? new Date(job.scheduling.startTime).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'TBD'}
                      {job.scheduling?.endTime && (
                        <>
                          {' - '}
                          {new Date(job.scheduling.endTime).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Est. Duration</p>
                    <p className="text-gray-900">
                      {job.estimatedDurationMinutes
                        ? `${job.estimatedDurationMinutes} minutes`
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Priority</p>
                    <p className="text-gray-900">
                      {job.priority <= 3 ? 'High' : job.priority <= 5 ? 'Normal' : 'Low'}
                      <span className="text-gray-400 ml-1">({job.priority})</span>
                    </p>
                  </div>
                </div>
                {job.scheduling?.appointmentDescription && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 uppercase mb-1">Appointment Notes</p>
                    <p className="text-gray-700">{job.scheduling.appointmentDescription}</p>
                  </div>
                )}
              </section>

              {/* Technicians */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Assigned Technicians
                </h4>
                {job.technicians && job.technicians.length > 0 ? (
                  <div className="space-y-4">
                    {job.technicians.map((tech, idx) => (
                      <div key={tech.id} className="flex items-start gap-3">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{tech.fullName}</p>
                            {idx === 0 && (
                              <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full font-medium">Lead</span>
                            )}
                          </div>
                          {tech.phone && (
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <a href={`tel:${tech.phone}`} className="hover:text-brand-600">{tech.phone}</a>
                            </div>
                          )}
                          {tech.email && (
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <a href={`mailto:${tech.email}`} className="hover:text-brand-600">{tech.email}</a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {job.assignmentReason && (
                      <p className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                        Assignment reason: {job.assignmentReason}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">No technicians assigned</p>
                )}
              </section>

              {/* Job Details */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Job Details
                </h4>
                <div className="space-y-3">
                  {job.jobSummary && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Summary</p>
                      <p className="text-gray-900">{job.jobSummary}</p>
                    </div>
                  )}
                  {job.description && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
                      <p className="text-gray-700">{job.description}</p>
                    </div>
                  )}
                  {job.requiredSkills && job.requiredSkills.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Required Skills</p>
                      <div className="flex flex-wrap gap-1">
                        {job.requiredSkills.map((skill, idx) => (
                          <span key={idx} className="badge bg-gray-100 text-gray-700">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Parts Information */}
              {job.parts && job.parts.status !== 'not_required' && (
                <section className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Parts
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className={clsx('badge', partsStatusColors[job.parts.status])}>
                        {partsStatusLabels[job.parts.status]}
                      </span>
                      {job.parts.eta && (
                        <span className="text-sm text-gray-500">
                          ETA: {new Date(job.parts.eta).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {job.parts.transferOrders.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {job.parts.transferOrders.map((to) => (
                          <div key={to.id}>
                            <p className="text-xs font-medium text-gray-500 mb-1">
                              {to.transferNumber} — <span className="capitalize">{to.status}</span>
                              {to.eta && ` · ETA ${new Date(to.eta).toLocaleDateString()}`}
                            </p>
                            {to.items.length > 0 && (
                              <TransferOrderItemsTable items={to.items} />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {job.parts.notes && (
                      <p className="text-sm text-gray-600 mt-2">{job.parts.notes}</p>
                    )}
                  </div>
                </section>
              )}

              {/* Warranty Information */}
              {job.warranty?.isWarrantyJob && (
                <section className="card p-4 border-orange-200 bg-orange-50">
                  <h4 className="text-sm font-semibold text-orange-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Warranty Job
                  </h4>
                  <div className="space-y-2">
                    {job.warranty.originalOrderId && (
                      <p className="text-sm text-orange-800">
                        Original Order: <span className="font-medium">{job.warranty.originalOrderId}</span>
                      </p>
                    )}
                    {job.warranty.serviceDates && job.warranty.serviceDates.length > 0 && (
                      <div>
                        <p className="text-xs text-orange-700 uppercase mb-1">Previous Service Dates</p>
                        <div className="flex flex-wrap gap-2">
                          {job.warranty.serviceDates.map((date, idx) => (
                            <span key={idx} className="text-sm bg-orange-100 px-2 py-1 rounded">
                              {new Date(date).toLocaleDateString()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {job.warranty.resolutionNotes && (
                      <div className="mt-2">
                        <p className="text-xs text-orange-700 uppercase mb-1">Resolution Notes</p>
                        <p className="text-sm text-orange-800">{job.warranty.resolutionNotes}</p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Internal Notes */}
              {job.internalNotes && (
                <section className="card p-4 bg-yellow-50 border-yellow-200">
                  <h4 className="text-sm font-semibold text-yellow-900 uppercase tracking-wider mb-2">
                    Internal Notes
                  </h4>
                  <p className="text-yellow-800">{job.internalNotes}</p>
                </section>
              )}

              {/* Metadata */}
              <section className="text-xs text-gray-400 pt-4 border-t border-gray-100">
                <div className="flex justify-between">
                  <span>SyteLine ID: {job.sytelineOrderId}</span>
                  <span>Created: {new Date(job.createdAt).toLocaleString()}</span>
                </div>
                {job.sytelineBillStatus && (
                  <p className="mt-1">Bill Status: {job.sytelineBillStatus}</p>
                )}
              </section>
            </div>
          ) : null}
        </div>

        {/* Footer Actions */}
        {job && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
            <div className="flex gap-2">
              {job.status === 'IN_PROGRESS' && (
                <button
                  onClick={handleCompleteJob}
                  disabled={completing}
                  className="btn-primary flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {completing ? 'Completing...' : 'Complete Job'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
